import {
  Entity,
  getEntitySourceLocation,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
} from '@backstage/catalog-model';
import {
  EntityRefLinks,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import { Chip, Grid, makeStyles } from '@material-ui/core';
import { MarkdownContent, Link } from '@backstage/core-components';
import React from 'react';
import { AboutField } from '@backstage/plugin-catalog';
import {
  containsBuildInfo,
  containsReleaseInfo,
  getBuildDate,
  getBuildText,
  getBuildUrl,
  getCommitId,
  getCommitUrl,
  getReleaseText,
  getReleaseUrl,
} from '../CatalogHelpers';
import {
  useApi,
} from '@backstage/core-plugin-api';
import { monitorApiRef, MonitorApi, HealthStatus } from '@internal/backstage-plugin-ewii-azure';
import { useAsync } from 'react-use';
import { ChangeLogModal } from '../../../../plugins/release-overview/components';
import { extractBaseFileName } from '../../../../plugins/release-overview/utils';

const useStyles = makeStyles({
  description: {
    wordBreak: 'break-word',
  },
  healthy: {
    color: 'green',
  },
  unhealthy: {
    color: 'red',
  },
});

/**
 * Props for {@link AboutContent}.
 *
 * @public
 */
export interface AboutContentProps {
  entity: Entity;
}

/** @public */
export function AboutContent(props: AboutContentProps) {
  const { entity } = props;
  const classes = useStyles();
  const isSystem = entity.kind.toLocaleLowerCase('en-US') === 'system';
  const isResource = entity.kind.toLocaleLowerCase('en-US') === 'resource';
  const isComponent = entity.kind.toLocaleLowerCase('en-US') === 'component';
  const isAPI = entity.kind.toLocaleLowerCase('en-US') === 'api';
  const isTemplate = entity.kind.toLocaleLowerCase('en-US') === 'template';
  const isLocation = entity.kind.toLocaleLowerCase('en-US') === 'location';
  const isGroup = entity.kind.toLocaleLowerCase('en-US') === 'group';
  const hasBuildInfo = containsBuildInfo(entity);
  const hasReleaseInfo = containsReleaseInfo(entity);
  const monitorApi = useApi(monitorApiRef) as MonitorApi;
  
  const { loading, error, value } = useAsync(async () => {
    if(isComponent) {
      const response = await monitorApi.getHealthStatusForComponent(entity.metadata.name);
      return response;
    }
    return [];
  }, [monitorApi, entity, isComponent]);

  const partOfSystemRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'system',
  });
  const partOfComponentRelations = getEntityRelations(
    entity,
    RELATION_PART_OF,
    {
      kind: 'component',
    },
  );
  const partOfDomainRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'domain',
  });
  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);

  let entitySourceLocation:
    | {
        type: string;
        target: string;
      }
    | undefined;
  try {
    entitySourceLocation = getEntitySourceLocation(entity);
  } catch (e) {
    entitySourceLocation = undefined;
  }

  function fetchHealthStatus(loading: boolean, error: Error | undefined, value: HealthStatus[] | undefined): string | undefined {
    if (loading) {
      return 'Loading...';
    } else if (error) {
      return `Error: ${error.message}`;
    } else if (value && value.length > 0) {
      const isHealthy = value.every(item => item.status === 'Healthy');
      if (isHealthy) {
        return 'Healthy';
      }
      return 'Unhealthy';
    }
    return undefined;
  }

  // Helper function to check if entity has change log information
  function hasChangeLogInfo(entity: Entity): boolean {
    return !!(
      entity.metadata?.annotations?.['ewii/change-log-file'] ||
      entity.metadata?.annotations?.['ewii/change-log-url']
    );
  }

  // Helper function to get change log file name
  function getChangeLogFileName(entity: Entity): string {
    const changeLogFile = entity.metadata?.annotations?.['ewii/change-log-file'];
    const changeLogUrl = entity.metadata?.annotations?.['ewii/change-log-url'];
    
    if (changeLogFile) {
      return extractBaseFileName(changeLogFile);
    } else if (changeLogUrl) {
      return extractBaseFileName(changeLogUrl);
    }
    return '';
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <AboutField label="Description">
          <MarkdownContent
            className={classes.description}
            content={entity?.metadata?.description || 'No description'}
          />
        </AboutField>
      </Grid>
      <Grid item xs={12} sm={6} lg={4}>
        <AboutField label="Owner" value="No Owner">
          {ownedByRelations.length > 0 && (
            <EntityRefLinks entityRefs={ownedByRelations} defaultKind="group" />
          )}
        </AboutField>
      </Grid>
      {(isSystem || partOfDomainRelations.length > 0) && (
        <Grid item xs={12} sm={6} lg={4}>
          <AboutField label="Domain" value="No Domain">
            {partOfDomainRelations.length > 0 && (
              <EntityRefLinks
                entityRefs={partOfDomainRelations}
                defaultKind="domain"
              />
            )}
          </AboutField>
        </Grid>
      )}
      {(isComponent || isResource || partOfSystemRelations.length > 0) && (
        <Grid item xs={12} sm={6} lg={4}>
          <AboutField label="System" value="No System">
            {partOfSystemRelations.length > 0 && (
              <EntityRefLinks
                entityRefs={partOfSystemRelations}
                defaultKind="system"
              />
            )}
          </AboutField>
        </Grid>
      )}
      {isComponent && partOfComponentRelations.length > 0 && (
        <Grid item xs={12} sm={6} lg={4}>
          <AboutField label="Parent Component" value="No Parent Component">
            <EntityRefLinks
              entityRefs={partOfComponentRelations}
              defaultKind="component"
            />
          </AboutField>
        </Grid>
      )}
      {(isAPI ||
        isComponent ||
        isResource ||
        isTemplate ||
        isGroup ||
        isLocation ||
        typeof entity?.spec?.type === 'string') && (
        <Grid item xs={12} sm={6} lg={4}>
          <AboutField label="Type" value={entity?.spec?.type as string} />
        </Grid>
      )}
      {(isAPI ||
        isComponent ||
        typeof entity?.spec?.lifecycle === 'string') && (
        <Grid item xs={12} sm={6} lg={4}>
          <AboutField
            label="Lifecycle"
            value={entity?.spec?.lifecycle as string}
          />
        </Grid>
      )}
      {hasBuildInfo && (
        <Grid item xs={12} sm={6} lg={4}>
          <AboutField label="Build" value="Ukendt">
            <Link to={getBuildUrl(entity)!} target="_blank">
              {getBuildText(entity)}
            </Link>
          </AboutField>
        </Grid>
      )}
      {hasBuildInfo && (
        <Grid item xs={12} sm={6} lg={4}>
          <AboutField label="Commit" value="Ukendt">
            <Link to={getCommitUrl(entity)!} target="_blank">
              {getCommitId(entity)}
            </Link>
          </AboutField>
        </Grid>
      )}
      {hasReleaseInfo && (
        <Grid item xs={12} sm={6} lg={4}>
          <AboutField label="Release" value="Ukendt">
            <Link to={getReleaseUrl(entity)!} target="_blank">
              {getReleaseText(entity)}
            </Link>
          </AboutField>
        </Grid>
      )}
      {hasReleaseInfo && (
        <Grid item xs={12} sm={6} lg={4}>
          <AboutField
            label="Release Date"
            value={getBuildDate(entity) || 'Ukendt'}
          />
        </Grid>
      )}
      {isComponent && (
        <Grid item xs={12} sm={6} lg={4}>
          <AboutField
            label="Health Status"
            value={fetchHealthStatus(loading, error, value)}
            className={
              fetchHealthStatus(loading, error, value) === 'Healthy'
                ? classes.healthy
                : fetchHealthStatus(loading, error, value) === 'Unhealthy'
                ? classes.unhealthy
                : undefined
            }
          />
        </Grid>
      )}
      {isComponent && hasChangeLogInfo(entity) && (
        <Grid item xs={12} sm={6} lg={4}>
          <AboutField label="Change Log" value="No Change Log">
            <ChangeLogModal
              component={entity.metadata.name}
              fileName={getChangeLogFileName(entity)}
              displayText=""
              fullRowExpansion={true}
            />
          </AboutField>
        </Grid>
      )}
      <Grid item xs={12} sm={6} lg={4}>
        <AboutField label="Tags" value="No Tags">
          {(entity?.metadata?.tags || []).map(t => (
            <Chip key={t} size="small" label={t} />
          ))}
        </AboutField>
      </Grid>
    </Grid>
  );
}
